/**
 * Retired platform-admin "act as org" state — cleanup only, never honored.
 *
 * Admins now manage every organization's studies directly (the backend authorizes the actual
 * signed-in principal per study), so nothing reads an acting org any more. Tabs and bookmarks from
 * before the change may still carry it: per-tab sessionStorage entries and `act_as_*` URL params on
 * old study links. Both are dropped on sight so a stale value can neither grant access nor leave
 * "acting" chrome behind; the study id in the URL alone decides what opens.
 */

const LEGACY_STORAGE_KEYS = ["lato.actAsOrg", "lato:act-as-return"] as const;
const LEGACY_SEARCH_KEYS = ["act_as_org", "act_as_name"] as const;

/** Forget any act-as state an older build left in this tab. Safe without storage access. */
export function clearLegacyActingState(): void {
  try {
    for (const key of LEGACY_STORAGE_KEYS) sessionStorage.removeItem(key);
  } catch {
    // Storage blocked (private mode, sandbox): there is nothing to clear.
  }
}

/** The search without legacy `act_as_*` params, or null when there were none to strip. */
export function stripLegacyActAsSearch<T extends Record<string, unknown>>(
  search: T
): Omit<T, (typeof LEGACY_SEARCH_KEYS)[number]> | null {
  if (!LEGACY_SEARCH_KEYS.some((key) => key in search)) return null;
  const rest: Record<string, unknown> = { ...search };
  for (const key of LEGACY_SEARCH_KEYS) delete rest[key];
  return rest as Omit<T, (typeof LEGACY_SEARCH_KEYS)[number]>;
}
